import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { ContactSearchComponent } from '../../common/contact-search/contact-search.component';

import { ContactService } from '../../../services/contacts/contact.service';
import { ContactStoreService } from '../../../services/contacts/contact-store.service';
import { ModalService } from '../../../services/common/modal.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-contact-sidebar',
  templateUrl: './contact-sidebar.component.html',
  styleUrls: ['./contact-sidebar.component.scss'],
})
export class ContactSidebarComponent implements OnInit, OnChanges {
  /**
   * Contact's id for the sidebar
   */
  @Input() contact_id: number;

  /**
   * Params for billings section query
   * @type {any}
   */
  billingParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact billings paginated
   * @type {Array<any> | undefined}
   */
  billings: Array<any> | undefined;

  /**
   * Params for bills section query
   * @type {any}
   */
  billParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact bills (receipts earned) paginated
   * @type {Array<any> | undefined}
   */
  bills: Array<any> |undefined;

  /**
   * contact disbursements (checks) paginated
   * @type {Array<any>}
   */
  checks: Array<any>;

  /**
   * Cloned contact id
   * @type {number | undefined}
   */
  clonedContactId: number | undefined;

  /**
   * Clone params for clone
   * @type {any}
   */
  cloneParams: any = {
    cloneOptionsCheck: {
      Addresses: true,
      'Email Addresses': true,
      'Phone Numbers': true,
    },
  };

  /**
   * Indicates if cloning is being done
   * @type {boolean}
   */
  cloning: boolean = false;

  /**
   * Indicates if the contact is being deleted
   * @type {boolean}
   */
  deleting: boolean = false;

  /**
   * Params for disbursements section query
   * @type {any}
   */
  disbursementParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * Params for documents section query
   * @type {any}
   */
  documentParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact documents paginated
   * @type {Array<any>}
   */
  documents: Array<any>;

  /**
   * Params for employees section query
   * @type {any}
   */
  employeeParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact employees paginated
   * @type {Array<any>}
   */
  employees: Array<any>;

  /**
   * Indicates if the contact is of an intranet user
   * @type {boolean}
   */
  isUser: boolean = false;

  /**
   * Indicates if contact billings is being loaded from backend
   * @type {boolean}
   */
  loadingBillings: boolean = true;

  /**
   * Indicates if contact bills (receipts earned) is being loaded from backend
   * @type {boolean}
   */
  loadingBills: boolean = true;

  /**
   * Indicates if contact checks (disbursements) is being loaded from backend
   * @type {boolean}
   */
  loadingDisbursements: boolean = true;

  /**
   * Indicates if contact documents is being loaded from backend
   * @type {boolean}
   */
  loadingDocuments: boolean = true;

  /**
   * Indicates if contact employees is being loaded from backend
   * @type {boolean}
   */
  loadingEmployees: boolean = true;

  /**
   * Indicates if contact matters is being loaded from backend
   * @type {boolean}
   */
  loadingMatters: boolean = true;

  /**
   * Indicates if contact receipts (recent receipts from) is being loaded from backend
   * @type {boolean}
   */
  loadingReceipts: boolean = true;

  /**
   * Indicates if the sidebar info is loading
   * @type {boolean}
   */
  loadingSidebarInfo: boolean = false;

  /**
   * Params for matters section query
   * @type {any}
   */
  matterParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact matters paginated
   * @type {Array<any>}
   */
  matters: Array<any>;

  /**
   * Merge params for merge
   * @type {any}
   */
  mergeParams: any = {};

  /**
   * Indicates if merging is being done
   * @type {boolean}
   */
  merging: boolean = false;

  /**
   * Params for receipts section query
   * @type {any}
   */
  receiptParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * contact receipts (recent receipts from) paginated
   * @type {Array<any>}
   */
  receipts: Array<any>;

  /**
   * Reminderable fields for contact
   * @type {Array<any>}
   */
  reminderableFields: Array<any> = [
    {
      label: 'New DOB Reminder',
      value: 'dob',
    },
  ];

  /**
   * Contact's static sidebar info
   * @type {any}
   */
  sidebarContactInfo: any;

  /**
   * Marking expanded sidebar sections using keys with boolean values in this object
   * @type {any}
   */
  sidebarExpand: any = {};

  /**
   * Merge contact search component
   * @param {[type]} 'contactSearch'
   */
  @ViewChild('mergeContactSearch') mergeContactSearchComponent: ContactSearchComponent;

  constructor(private contactService: ContactService, private contactStoreService: ContactStoreService, private modalService: ModalService, private notyService: NotyService, private router: Router) {
    this.contactStoreService.contactSidebarComponent = this;
  }

  // Component lifecycle methods
  ngOnInit() {
    if (this.contact_id) {
      this.loadContact();
      this.mergeParams.winner_id = this.contact_id;
      this.cloneParams.id = this.contact_id;
    }
  }

  /**
   * Angular Component lifecycle method which is called when one or more member variable change
   * @param {SimpleChanges} changes Object with all changes done to member variables of this class
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.contact_id && changes.contact_id.currentValue) {
      this.loadContact();
      this.mergeParams.winner_id = this.contact_id;
      this.cloneParams.id = this.contact_id;
    }
  }

  // Component methods

  /**
   * Clone contact
   */
  clone() {
    this.cloning = true;
    this.cloneParams.clone_options = [];
    for (let key in this.cloneParams.cloneOptionsCheck) if (this.cloneParams.cloneOptionsCheck.hasOwnProperty(key) && this.cloneParams.cloneOptionsCheck[key]) this.cloneParams.clone_options.push(key);
    this.contactService
      .clone(this.cloneParams)
      .then((response_json) => {
        this.cloning = false;
        this.clonedContactId = undefined;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Contact cloned', 'success', 5000, 'topRight');
          if (response_json.id) this.router.navigateByUrl(`/contacts/${response_json.id}`);
        } else this.notyService.notify(response_json.text || 'Error cloning contact', 'error', 5000, 'topRight');
      })
      .catch(() => {
        this.cloning = false;
        this.notyService.notify('Error cloning contact', 'error', 5000, 'topRight');
        this.clonedContactId = undefined;
      });
  }

  /**
   * Delete contact
   */
  deleteContact() {
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Delete this Contact ?</p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          this.deleting = true;
          this.contactService
            .delete(this.contact_id)
            .then((response_json) => {
              this.deleting = false;
              if (response_json.success) {
                this.notyService.notify(response_json.text || 'Contact deleted', 'info', 5000, 'topRight');
                this.router.navigateByUrl('/contacts');
              } else this.notyService.notify(response_json.text || 'Error deleting contact', 'error', 5000, 'topRight');
            })
            .catch(() => {
              this.deleting = false;
              this.notyService.notify('Error deleting contact', 'error', 5000, 'topRight');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Load paginated billings
   * @param {number = null} page new page number
   */
  loadBillings(page: number | null = null) {
    if (page) this.billingParams.page = page;
    this.loadingBillings = true;
    this.contactService
      .billings(this.contact_id, this.billingParams)
      .then((response_json) => {
        this.loadingBillings = false;
        this.billings = response_json;
      })
      .catch(() => {
        this.loadingBillings = false;
        this.notyService.notify('Error loading billings');
      });
  }

  /**
   * Load paginated bills
   * @param {number = null} page new page number
   */
  loadBills(page: number | null = null) {
    if (page) this.billParams.page = page;
    this.loadingBills = true;
    this.contactService
      .bills(this.contact_id, this.billParams)
      .then((response_json) => {
        this.loadingBills = false;
        this.bills = response_json;
        console.log('bills', this.bills, response_json);
      })
      .catch(() => {
        this.loadingBills = false;
        this.notyService.notify('Error loading bills');
      });
  }

  /**
   * Load all sections of the sidebar
   */
  loadContact(loadAll: boolean = true) {
    this.loadingSidebarInfo = true;
    this.contactService
      .contactSidebarInfo(this.contact_id)
      .then((response_json) => {
        this.loadingSidebarInfo = false;
        this.sidebarContactInfo = response_json;
        this.isUser = response_json.is_user;
        if (!loadAll) return;
        if (this.sidebarContactInfo.billing_count) {
          this.billingParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
          this.loadBillings();
        } else this.billings = undefined;
        if (this.sidebarContactInfo.bill_count) {
          this.billParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
          this.loadBills();
        } else this.bills = undefined;
        this.disbursementParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
        this.loadDisbursements();
        this.documentParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
        this.loadDocuments();
        this.employeeParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
        this.loadEmployees();
        this.matterParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
        this.loadMatters();
        this.receiptParams = { page: 1, per_page: this.sidebarContactInfo.per_page || 20 };
        this.loadReceipts();
      })
      .catch(() => {
        this.loadingSidebarInfo = false;
      });
  }

  /**
   * Load paginated disbursements (checks)
   * @param {number = null} page new page number
   */
  loadDisbursements(page: number | null = null) {
    if (page) this.disbursementParams.page = page;
    this.loadingDisbursements = true;
    this.contactService
      .disbursements(this.contact_id, this.disbursementParams)
      .then((response_json) => {
        this.loadingDisbursements = false;
        this.checks = response_json;
      })
      .catch(() => {
        this.loadingDisbursements = false;
        this.notyService.notify('Error loading disbursements');
      });
  }

  /**
   * Load paginated documents
   * @param {number = null} page new page number
   */
  loadDocuments(page: number | null = null) {
    if (page) this.documentParams.page = page;
    this.loadingDocuments = true;
    this.contactService
      .documents(this.contact_id, this.documentParams)
      .then((response_json) => {
        this.loadingDocuments = false;
        this.documents = response_json;
      })
      .catch(() => {
        this.loadingDocuments = false;
        this.notyService.notify('Error loading documents');
      });
  }

  /**
   * Load paginated employees
   * @param {number = null} page new page number
   */
  loadEmployees(page: number | null = null) {
    if (page) this.employeeParams.page = page;
    this.loadingEmployees = true;
    this.contactService
      .employees(this.contact_id, this.employeeParams)
      .then((response_json) => {
        this.loadingEmployees = false;
        this.employees = response_json;
      })
      .catch(() => {
        this.loadingEmployees = false;
        this.notyService.notify('Error loading employee list');
      });
  }

  /**
   * Load paginated matters
   * @param {number = null} page new page number
   */
  loadMatters(page: number | null = null) {
    if (page) this.matterParams.page = page;
    this.loadingMatters = true;
    this.contactService
      .matters(this.contact_id, this.matterParams)
      .then((response_json) => {
        this.loadingMatters = false;
        this.matters = response_json;
      })
      .catch(() => {
        this.loadingMatters = false;
        this.notyService.notify('Error loading matters');
      });
  }

  /**
   * Load paginated receipts
   * @param {number = null} page new page number
   */
  loadReceipts(page: number | null = null) {
    if (page) this.receiptParams.page = page;
    this.loadingReceipts = true;
    this.contactService
      .receipts(this.contact_id, this.receiptParams)
      .then((response_json) => {
        this.loadingReceipts = false;
        this.receipts = response_json;
      })
      .catch(() => {
        this.loadingReceipts = false;
        this.notyService.notify('Error loading receipts');
      });
  }

  /**
   * Merge contact
   */
  merge() {
    this.merging = true;
    this.contactService
      .merge(this.mergeParams)
      .then((response_json) => {
        this.merging = false;
        if (response_json && response_json.errors) this.notyService.notify(response_json.errors || 'Error merging contact', 'error', 5000, 'topRight');
        else {
          this.notyService.notify('Contact merged', 'success', 5000, 'topRight');
          this.mergeParams.loser_id = undefined;
          if (this.mergeContactSearchComponent) this.mergeContactSearchComponent.clearSelectedContact();
          if (this.contactStoreService && this.contactStoreService.contactShowComponent) this.contactStoreService.contactShowComponent.loadContact();
          this.loadContact();
        }
      })
      .catch((err) => {
        console.error(err);
        this.merging = false;
        this.notyService.notify('Error merging contact', 'error', 5000, 'topRight');
      });
  }

  contactFormSaveEventHander($event: any) {
    if($event.status){
      this.sidebarExpand.edit = !(this.sidebarExpand.edit)
    }
  }

  /**
   * Download document - uses anchor element for reliable download
   * @param {Event} event Click event object
   * @param {number} documentId Document ID to download
   */
  downloadDocument(event: Event, documentId: number): boolean {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const token = window.localStorage.getItem('token');

    if (!token) {
      console.error('No authentication token found');
      this.notyService.notify('Authentication token not found. Please log in again.', 'error');
      return false;
    }

    const url = `/documents/${documentId}/download?disposition=attachment&token=${encodeURIComponent(token)}`;
    this.downloadInBackground(url);

    return false;
  }

  downloadInBackground(url: string): void {
    const iframeId = 'contact-document-download-frame';
    let iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = iframeId;
      iframe.name = iframeId;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    iframe.src = url;
  }
}
